import { AxiosRequestConfig } from "axios";

import { moneefloApi } from "./client";

/**
 * Customer registration: phone, PAN, Aadhaar.
 *
 * Xano ran this as a numbered sequence of endpoints — step 1, step 2,
 * step 2 fallback, step 3, step 3 fallback — each writing a bit more onto a
 * half-built customer. The new API has no notion of a step: there is a
 * customer, there are documents against them, and there are KYC checks. So
 * these keep the old names and shapes for the screen's sake and translate.
 *
 * The screen reads quite specific paths off these responses — the PAN name
 * from `result1.full_name`, the Aadhaar address from
 * `adhaar.back.document_fields.address` — so those shapes are rebuilt
 * faithfully rather than simplified. Changing them means changing the
 * screen, which is a separate job from getting it working again.
 *
 * No OTP. The two OTP endpoints Xano had are gone: the desk photographs
 * what the customer hands over, and an OTP to the Aadhaar-linked mobile is
 * not something a walk-in can always answer. The screen's OTP modal was
 * already unreachable — nothing ever set it visible.
 */

type Payload<T> = AxiosRequestConfig<T>;

/* ------------------------------------------------------- images on the wire */

/**
 * The screen holds its uploads as base64 data URLs, because that is what
 * Xano took. The new endpoint takes multipart, which is what an image
 * should be, so they are turned back into bytes here rather than making
 * every caller change.
 */
function dataUrlToBlob(dataUrl: string): Blob | null {
  if (!dataUrl) return null;
  const match = /^data:([^;,]+)?(;base64)?,(.*)$/s.exec(dataUrl);
  if (!match) return null;

  const contentType = match[1] || "application/octet-stream";
  const isBase64 = Boolean(match[2]);
  const payload = match[3] ?? "";

  try {
    if (!isBase64) {
      return new Blob([decodeURIComponent(payload)], { type: contentType });
    }
    const binary = atob(payload);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: contentType });
  } catch {
    // A truncated or malformed data URL: treated as "no image", which the
    // callers already handle, rather than throwing inside a form submit.
    return null;
  }
}

/**
 * Cashfree reads JPEG, JPG and PNG. Nothing else.
 *
 * The desk uploads whatever the file on the counter happens to be, and a
 * PAN card saved as AVIF came back "Provide valid file or file url" —
 * which the screen shows as its manual-entry fallback, so it reads as
 * "the PAN could not be verified" rather than "that file format cannot
 * be read". The browser can already decode anything it renders, so
 * anything that is not already a JPEG or a PNG is drawn to a canvas and
 * re-encoded here, before it ever leaves.
 *
 * Also caps the long edge. A phone photograph is twelve megapixels of
 * which the card is a tenth; sending it whole is slower for the desk and
 * no more readable.
 *
 * Returns the original when the browser cannot decode it — an HEIC in
 * Chrome, say. Cashfree will refuse it, and its own message about the
 * format is more use than a conversion error from us.
 */
const MAX_EDGE = 2000;

async function asReadableImage(blob: Blob): Promise<Blob> {
  const alreadyFine = blob.type === "image/jpeg" || blob.type === "image/png";
  if (blob.type === "application/pdf") return blob;
  if (alreadyFine && blob.size <= 4 * 1024 * 1024) return blob;

  const url = URL.createObjectURL(blob);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error("cannot decode"));
      element.src = url;
    });

    const scale = Math.min(
      1,
      MAX_EDGE / Math.max(image.naturalWidth, image.naturalHeight),
    );
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(image.naturalWidth * scale);
    canvas.height = Math.round(image.naturalHeight * scale);
    const context = canvas.getContext("2d");
    if (!context || canvas.width === 0 || canvas.height === 0) return blob;
    // A JPEG has no alpha; without this a transparent PNG goes to black
    // and the document is unreadable.
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    const jpeg = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.92),
    );
    return jpeg ?? blob;
  } catch {
    return blob;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function extensionFor(blob: Blob): string {
  if (blob.type === "image/png") return "png";
  if (blob.type === "image/webp") return "webp";
  if (blob.type === "application/pdf") return "pdf";
  return "jpg";
}

/* ---------------------------------------------------------------- step one */

export type GetCustomerPhoneNumberDetailsRequestType = Partial<{
  headers: { Authorization: string };
  number: string;
  params: { phone_number: string; number: string };
}>;

/** The name the screen imports it under. */
export type GetCustomerPhoneNumberRequestType =
  GetCustomerPhoneNumberDetailsRequestType;

/**
 * The phone box's typeahead: people we already hold.
 *
 * The screen calls it with `params: { number }`; Xano's own signature used
 * `phone_number`. Both are read, because getting that wrong is silent —
 * the search runs on an empty string and the dropdown simply never
 * appears, which looks like "we have no record of them".
 *
 * It fires on every keystroke, so a failure is swallowed: a lookup that
 * could not run is not something to interrupt someone mid-number with,
 * and the caller renders nothing on an empty list.
 */
export async function getCustomerPhoneNumberDetails(
  payload: Payload<GetCustomerPhoneNumberDetailsRequestType> & {
    params?: { phone_number?: string; number?: string };
  },
): Promise<{ data: Record<string, unknown>[] }> {
  const search = String(
    payload.params?.number ??
      payload.params?.phone_number ??
      payload.data?.number ??
      payload.data?.params?.phone_number ??
      "",
  ).trim();
  if (search.length < 6) return { data: [] };

  const { data } = await moneefloApi
    .get("/customers", {
      params: { search, perPage: 10 },
    })
    .catch(() => ({ data: { items: [] } }));
  const rows = (data?.items ?? []) as {
    id: string;
    fullName: string;
    email: string | null;
    phone: string | null;
  }[];

  return {
    data: rows.map((row) => ({
      id: row.id,
      customers_id: row.id,
      contacts_id: row.id,
      full_name: row.fullName,
      name: row.fullName,
      email: row.email ?? "",
      phone_number: row.phone ?? "",
      number: row.phone ?? "",
    })),
  };
}

export type PostCustomerRegistrationStep1RequestType = Partial<{
  headers: { Authorization: string };
  email: string;
  number: string;
  passport_no: string;
  contacts_id: string | number;
}>;

/**
 * Opens the registration: the person, by phone.
 *
 * Xano created a half-built customer row here. We look first — a contact
 * the desk already spoke to is the same person, and registering them again
 * would be a second record for one human — and only create when the number
 * is new to us.
 */
export async function postCustomerRegistrationStep1(
  payload: Payload<PostCustomerRegistrationStep1RequestType>,
): Promise<{
  status: number;
  data: {
    id: string;
    /** We already held this number; the registration is being resumed. */
    existing?: boolean;
    /** Their name, when we have a real one rather than the placeholder. */
    existingName?: string | null;
    /** Whether that existing record has already finished KYC. */
    existingVerified?: boolean;
  };
}> {
  const body = payload.data ?? {};
  const phone = String(body.number ?? "").trim();
  const email = String(body.email ?? "").trim();
  const existingId = String(body.contacts_id ?? "").trim();

  if (existingId) {
    // Came in from a contact, so the person is already on file.
    if (email) {
      await moneefloApi
        .patch(`/customers/${existingId}`, { email })
        .catch(() => undefined);
    }
    return { status: 200, data: { id: existingId, existing: true } };
  }

  if (phone) {
    const { data } = await moneefloApi.get("/customers", {
      params: { search: phone, perPage: 10 },
    });
    const match = (
      (data?.items ?? []) as {
        id: string;
        phone?: string;
        fullName?: string;
        kycStatus?: string;
        type?: string;
      }[]
    ).find(
      // A retail registration resumes a retail person only: a company's
      // customer row carries the contact's number and must never be picked up.
      (row) => row.phone === phone && row.type !== "business",
    );
    if (match) {
      // Resumed, not duplicated — but the desk is told, because a number
      // they believed was new belonging to somebody already on file is
      // the one thing here they would want to stop and check.
      const name =
        match.fullName && match.fullName !== "Unnamed remitter"
          ? match.fullName
          : null;
      return {
        status: 200,
        data: {
          id: match.id,
          existing: true,
          existingName: name,
          existingVerified: match.kycStatus === "verified",
        },
      };
    }
  }

  const { data } = await moneefloApi.post("/customers", {
    // The name is not known yet: it comes off the identity document a
    // step later. A placeholder here is what the verified name replaces.
    fullName: "Unnamed remitter",
    type: "individual",
    phone: phone || undefined,
    email: email || undefined,
    source: "registration",
  });
  return { status: 200, data: { id: (data as { id: string }).id } };
}

export type GetStepsViaCustomerIdRequestType = Partial<{
  headers: { Authorization: string };
  params: { customers_id: string | number };
}>;

/**
 * How far a half-finished registration got.
 *
 * Derived from what is on file rather than stored as a number: a customer
 * with a verified PAN is past step 2 whether or not anything wrote that
 * down, and a counter can disagree with reality after a failed step.
 */
export async function getStepsViaCustomerId(
  payload: Payload<GetStepsViaCustomerIdRequestType> & {
    params?: { customers_id?: string | number };
  },
): Promise<{ data: { step: number; customers_id: string } }> {
  const id = String(
    payload.params?.customers_id ?? payload.data?.params?.customers_id ?? "",
  ).trim();
  if (!id) return { data: { step: 1, customers_id: "" } };

  const { data } = await moneefloApi.get(`/customers/${id}`);
  const identities = ((data as { identities?: { docType: string }[] })
    .identities ?? []) as { docType: string }[];

  const has = (docType: string): boolean =>
    identities.some((identity) => identity.docType === docType);

  // 1 phone, 2 PAN, 3 Aadhaar, 4 done.
  const step = has("aadhaar") ? 4 : has("pan") ? 3 : 2;
  return { data: { step, customers_id: id } };
}

/* ------------------------------------------------------------------- PAN */

export type PostCustomerRegistrationStep2RequestType = Partial<{
  headers: { Authorization: string };
  pan_card: string;
  pan_no: string;
  customers_id: string | number;
}>;

/**
 * Reads a photographed PAN card.
 *
 * The screen wants the name back under `result1.full_name` and the number
 * under `pan_no`, which is the shape Xano answered with.
 */
export async function POSTPanVerify(
  payload: Payload<PostCustomerRegistrationStep2RequestType>,
): Promise<{ status: number; data: Record<string, unknown> }> {
  const body = payload.data ?? {};
  const id = String(body.customers_id ?? "").trim();
  if (!id) throw new Error("Start with the customer's phone number.");

  const original = dataUrlToBlob(String(body.pan_card ?? ""));
  if (!original) throw new Error("Upload a photo of the PAN card.");
  const image = await asReadableImage(original);

  const form = new FormData();
  form.append("docType", "pan");
  form.append("front", image, `pan.${extensionFor(image)}`);

  const { data } = await moneefloApi.post(
    `/customers/${id}/kyc/verify`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  const result = data as {
    verified: boolean;
    message: string | null;
    person: { name?: string; documentNumber?: string; dob?: string } | null;
  };

  if (!result.verified) {
    // The screen opens its manual-entry form when this rejects, so the
    // provider's own words are what the operator reads.
    throw new Error(result.message || "That PAN card could not be read.");
  }

  const name = result.person?.name ?? "";
  return {
    status: 200,
    data: {
      full_name: name,
      result1: { full_name: name },
      pan_no: result.person?.documentNumber ?? "",
      dob: result.person?.dob ?? "",
    },
  };
}

export type PostCustomerRegistrationStep2FallbackReqType = Partial<{
  headers: { Authorization: string };
  customers_id: string | number;
  pan_no: string;
  name: string;
  pan_card: string;
}>;

/**
 * The PAN typed in by hand, when the photograph could not be read.
 *
 * The scan is still filed: an unreadable photo is still the document the
 * customer produced, and the desk is asserting what it says.
 */
export async function POSTPanVerifyFallback(
  payload: Payload<PostCustomerRegistrationStep2FallbackReqType>,
): Promise<{ status: number; data: Record<string, unknown> }> {
  const body = payload.data ?? {};
  const id = String(body.customers_id ?? "").trim();
  const panNo = String(body.pan_no ?? "").trim().toUpperCase();
  const name = String(body.name ?? "").trim();
  if (!id) throw new Error("Start with the customer's phone number.");
  if (!panNo) throw new Error("Enter the PAN number.");

  await uploadScan(id, String(body.pan_card ?? ""), "pan");
  await moneefloApi.post(`/customers/${id}/identities`, {
    docType: "pan",
    value: panNo,
    nameOnDoc: name || undefined,
  });
  if (name) await renameIfPlaceholder(id, name);

  return { status: 200, data: { pan_no: panNo, full_name: name } };
}

/* --------------------------------------------------------------- Aadhaar */

export type PostCustomerRegistrationStep3RequestType = Partial<{
  headers: { Authorization: string };
  customers_id: string | number;
  /** The main path spells these without the suffix; the fallback with. */
  aadhar_front: string;
  aadhar_back: string;
  aadhar_front_img: string;
  aadhar_back_img: string;
}>;

/**
 * Reads a photographed Aadhaar, both sides.
 *
 * The back carries the address, which is the only reason it is asked for.
 * The screen reads the answer out of `adhaar.front.document_fields` and
 * `adhaar.back.document_fields`, so that nesting is rebuilt here.
 */
export async function postCustomerRegistrationStep3(
  payload: Payload<PostCustomerRegistrationStep3RequestType>,
): Promise<{ status: number; data: Record<string, unknown> }> {
  const body = payload.data ?? {};
  const id = String(body.customers_id ?? "").trim();
  if (!id) throw new Error("Start with the customer's phone number.");

  const frontOriginal = dataUrlToBlob(
    String(body.aadhar_front ?? body.aadhar_front_img ?? ""),
  );
  const backOriginal = dataUrlToBlob(
    String(body.aadhar_back ?? body.aadhar_back_img ?? ""),
  );
  if (!frontOriginal) throw new Error("Upload the front of the Aadhaar card.");
  const front = await asReadableImage(frontOriginal);
  const back = backOriginal ? await asReadableImage(backOriginal) : null;

  const form = new FormData();
  form.append("docType", "aadhaar");
  form.append("front", front, `aadhaar-front.${extensionFor(front)}`);
  if (back) form.append("back", back, `aadhaar-back.${extensionFor(back)}`);

  const { data } = await moneefloApi.post(
    `/customers/${id}/kyc/verify`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  const result = data as {
    verified: boolean;
    message: string | null;
    person: {
      name?: string;
      dob?: string;
      address?: string;
      documentNumber?: string;
    } | null;
  };

  if (!result.verified) {
    throw new Error(result.message || "That Aadhaar card could not be read.");
  }

  return {
    status: 200,
    data: {
      adhaar: {
        front: {
          document_fields: {
            uid: result.person?.documentNumber ?? "",
            name: result.person?.name ?? "",
            dob: result.person?.dob ?? "",
          },
        },
        back: {
          document_fields: { address: result.person?.address ?? "" },
        },
      },
    },
  };
}

export type PostCustomerRegistrationStep3FallbackReqType = Partial<{
  headers: { Authorization: string };
  customers_id: string | number;
  aadhar_number: string;
  name: string;
  date: string;
  address: string;
  aadhar_front_img: string;
  aadhar_back_img: string;
}>;

/** The Aadhaar typed in by hand, when the photographs could not be read. */
export async function POSTAadhaarVerifyFallback(
  payload: Payload<PostCustomerRegistrationStep3FallbackReqType>,
): Promise<{ status: number; data: Record<string, unknown> }> {
  const body = payload.data ?? {};
  const id = String(body.customers_id ?? "").trim();
  const number = String(body.aadhar_number ?? "").replace(/\s+/g, "");
  const name = String(body.name ?? "").trim();
  if (!id) throw new Error("Start with the customer's phone number.");
  if (!number) throw new Error("Enter the Aadhaar number.");

  await uploadScan(id, String(body.aadhar_front_img ?? ""), "aadhaar");
  await uploadScan(id, String(body.aadhar_back_img ?? ""), "aadhaar_back");

  await moneefloApi.post(`/customers/${id}/identities`, {
    docType: "aadhaar",
    value: number,
    nameOnDoc: name || undefined,
  });

  // Typed by a person who is looking at the card, so it is written even
  // over what OCR may have guessed earlier.
  await moneefloApi
    .patch(`/customers/${id}`, {
      dob: isoDate(String(body.date ?? "")) || undefined,
      addressLine1: String(body.address ?? "").trim() || undefined,
    })
    .catch(() => undefined);
  if (name) await renameIfPlaceholder(id, name);

  return { status: 200, data: { aadhar_number: number, full_name: name } };
}

export type PostNameMismatchUpdateRequestType = Partial<{
  headers: { Authorization: string };
  customers_id: string | number;
  name: string;
}>;

/**
 * The name the desk settled on when the two documents disagree.
 *
 * The operator has both cards in front of them and has ticked to say which
 * is right, so this is an assertion, not a guess: it overwrites.
 */
export async function postNameMismatchUpdate(
  payload: Payload<PostNameMismatchUpdateRequestType>,
): Promise<{ status: number; data: unknown }> {
  const body = payload.data ?? {};
  const id = String(body.customers_id ?? "").trim();
  const name = String(body.name ?? "").trim();
  if (!id || !name) return { status: 200, data: null };

  const { data } = await moneefloApi.patch(`/customers/${id}`, {
    fullName: name,
  });
  return { status: 200, data };
}

/* ------------------------------------------------------------- the rest */

export type PostCustomerRegistrationStep4RequestType = Partial<{
  headers: { Authorization: string };
  customers_id: string | number;
  /** Left from the bank step that was taken out of the flow. */
  cancel_cheque: string;
  bank_account_no: string;
  bank_name: string;
  ifsc_code: string;
  nameMismatch: boolean;
}>;

/**
 * The bank-account step, which no longer exists.
 *
 * Its screen was removed from the flow and its `case` commented out, but
 * the call is still made at the end of the Aadhaar popup, with every field
 * empty. Answering rather than failing keeps the last step from erroring
 * on nothing; when that call is removed from the screen this can go.
 */
export async function postCustomerRegistrationStep4(
  payload: Payload<PostCustomerRegistrationStep4RequestType>,
): Promise<{ status: number; data: { id: string; status: string } }> {
  // The screen navigates to /customerdetails/:id off this, so it answers
  // with the customer it was given rather than a fabricated application.
  const id = String(payload.data?.customers_id ?? "").trim();
  return { status: 200, data: { id, status: "registered" } };
}

/* ---------------------------------------------------------------- helpers */

/** dd-mm-yyyy or dd/mm/yyyy, as typed on the fallback form, to ISO. */
function isoDate(value: string): string | null {
  const text = value.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  const match = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/.exec(text);
  if (!match) return null;
  const [, d, m, y] = match;
  return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
}

/** Files a scan against the customer, if there is one to file. */
async function uploadScan(
  customerId: string,
  dataUrl: string,
  docType: string,
): Promise<void> {
  const original = dataUrlToBlob(dataUrl);
  if (!original) return;
  const blob = await asReadableImage(original);

  const form = new FormData();
  form.append("docType", docType);
  form.append("file", blob, `${docType}.${extensionFor(blob)}`);
  await moneefloApi
    .post(`/customers/${customerId}/documents`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    // The registration must not fail because a scan would not store; the
    // identity is the thing being captured and the desk can re-upload.
    .catch(() => undefined);
}

/**
 * Names the customer, but never over a real name.
 *
 * A registration opens with "Unnamed remitter" because the name comes off
 * the document. Once a person has one, a later document disagreeing with
 * it is for someone to reconcile, not for the last write to win.
 */
async function renameIfPlaceholder(
  customerId: string,
  name: string,
): Promise<void> {
  try {
    const { data } = await moneefloApi.get(`/customers/${customerId}`);
    const current = (data as { fullName?: string }).fullName ?? "";
    if (current && current !== "Unnamed remitter") return;
    await moneefloApi.patch(`/customers/${customerId}`, { fullName: name });
  } catch {
    // Nothing here is worth failing the registration over.
  }
}
